import React, { useCallback } from 'react';
import classNames from 'classnames';
import { gql } from '@apollo/client';
import { PromoBanner, useBreakpoint } from '@moda/om';
import { isClientSide } from '@moda/portal-stanchions';
import sha from 'sha.js';
import { Link } from '../Link';
import {
  SitePromoBannerFragment,
  SitePromoBannerPumoModuleFragment,
  SitePromoBannerPumoModuleSlideFragment
} from '../../generated/types';
import { stripHTMLTags } from '../NormalizePumoStyles';
import { tc } from '../../lib/trackingContext';
import { useEnableClubModa } from '../../hooks/useEnableClubModa';
import { Crossfader } from '../Crossfader';
import { SitePromoBannerCountrySelector } from './SitePromoBannerCountrySelector';
import { SitePromoBannerLink } from './SitePromoBannerLink';

import './SitePromoBanner.scss';

const SITE_PROMO_BANNER_PUMO_MODULE_SLIDE_FRAGMENT = gql`
  fragment SitePromoBannerPumoModuleSlideFragment on Slide {
    ctaUrl
    slideText {
      ctaText
      taglineText {
        body
      }
    }
  }
`;

const SITE_PROMO_BANNER_PUMO_MODULE_FRAGMENT = gql`
  fragment SitePromoBannerPumoModuleFragment on SinglePumoModule {
    platform
    slides {
      ...SitePromoBannerPumoModuleSlideFragment
    }
  }
  ${SITE_PROMO_BANNER_PUMO_MODULE_SLIDE_FRAGMENT}
`;

export const SITE_PROMO_BANNER_FRAGMENT = gql`
  fragment SitePromoBannerFragment on DisplayPage {
    targetBuckets {
      name
      modules {
        ...SitePromoBannerPumoModuleFragment
      }
    }
  }
  ${SITE_PROMO_BANNER_PUMO_MODULE_FRAGMENT}
`;

const SITE_PROMO_BANNER_FALLBACK_MESSAGE = 'Discover new collections at Moda Operandi';

const BANNER_DURATION_MS = 4000;
const BANNER_TRANSITION_MS = 1400;

const findPromoText = (text: string | null | undefined) => {
  if (!isClientSide()) return text;
  if (text == null) return undefined;
  try {
    const element = document.createElement('div');
    element.innerHTML = text;
    return (element.textContent || '').trim();
  } catch {
    return text;
  }
};

interface Props {
  displayPage?: SitePromoBannerFragment | null;
}

export const SitePromoBanner: React.FC<Props> = ({ displayPage }) => {
  const isMobile = useBreakpoint({ lt: 'md' }).matches;

  const enableClubModa = useEnableClubModa();

  const bannerModules = displayPage?.targetBuckets.find(({ name }) => name === 'banner')
    ?.modules as SitePromoBannerPumoModuleFragment[];

  const modulesByPlatform = (bannerModules || []).reduce(
    (obj, currModule) => {
      if (isMobile && currModule.platform.includes('mobile')) {
        return { ...obj, mobile: [...obj.mobile, currModule] };
      } else if (!isMobile && currModule.platform.includes('desktop')) {
        return { ...obj, desktop: [...obj.desktop, currModule] };
      } else {
        return obj;
      }
    },
    { mobile: [], desktop: [] } as Record<string, SitePromoBannerPumoModuleFragment[]>
  );

  const bannerSlides: SitePromoBannerPumoModuleSlideFragment[] | null = (
    modulesByPlatform[isMobile ? 'mobile' : 'desktop']?.[0] as SitePromoBannerPumoModuleFragment
  )?.slides;

  const trackHrefClick = useCallback(
    (taglineText: string, promoId: string, event: React.MouseEvent) => {
      tc.track('Promotion Clicked', {
        addToTrackingEvent: {
          position: 'promo_banner',
          name: taglineText,
          promotion_id: promoId,
          label: promoId,
          coordinateX: event?.pageX || 0,
          coordinateY: event?.pageY || 0
        }
      });
    },
    []
  );

  return (
    <PromoBanner
      role="banner"
      className={classNames('SitePromoBanner', { 'SitePromoBanner--club': enableClubModa })}
      data-test-id="site-promo-banner"
    >
      <div>
        <SitePromoBannerCountrySelector className="SitePromoBanner__country-selector" />
      </div>

      <div>
        {bannerSlides && bannerSlides.length > 0 ? (
          <Crossfader
            className="SitePromoBanner__cell"
            duration={BANNER_DURATION_MS}
            transition={BANNER_TRANSITION_MS}
            active={bannerSlides.length > 1}
          >
            {bannerSlides.map(slide => (
              <Link
                key={slide?.slideText?.taglineText?.body || SITE_PROMO_BANNER_FALLBACK_MESSAGE}
                className="SitePromoBanner__cta"
                to={slide?.ctaUrl || '/new'}
                onClick={event =>
                  trackHrefClick(
                    findPromoText(slide?.slideText?.taglineText?.body) || '',
                    slide?.slideText?.ctaText ||
                      sha('sha256')
                        .update(slide?.slideText?.taglineText?.body || '')
                        .digest('hex'),
                    event
                  )
                }
                title={stripHTMLTags(
                  slide?.slideText?.taglineText?.body || SITE_PROMO_BANNER_FALLBACK_MESSAGE
                )}
                dangerouslySetInnerHTML={{
                  __html: stripHTMLTags(
                    slide?.slideText?.taglineText?.body || SITE_PROMO_BANNER_FALLBACK_MESSAGE
                  )
                }}
              />
            ))}
          </Crossfader>
        ) : (
          <Link
            className="SitePromoBanner__cta"
            to="/new"
            title={SITE_PROMO_BANNER_FALLBACK_MESSAGE}
          >
            {SITE_PROMO_BANNER_FALLBACK_MESSAGE}
          </Link>
        )}
      </div>

      <div>
        <SitePromoBannerLink />
      </div>
    </PromoBanner>
  );
};
